import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Form,
  Progress,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import SelectSearchFile from "./SelectSearchFile";
import {
  bulkSearch,
  bulkSearchProgress,
  downloadBulkSearchTemplate,
} from "common/services/search.service";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

/** How often the running search is asked how far it has got. */
const POLL_INTERVAL_MS = 1500;

/**
 * Identifies this run to the progress endpoint. Generated here, before the
 * upload starts, so polling can begin immediately rather than waiting for a
 * response that only arrives once the whole search is finished.
 */
const newSearchId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  // randomUUID needs a secure context; this fallback only has to be unique
  // per user, since the id is scoped to the caller server-side.
  return `bs-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/** "1h 4m", "2m 30s", "45s" — kept short enough to sit on one line. */
const formatDuration = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

type SearchStatus = {
  found?: boolean;
  phase?: string;
  label?: string;
  percent?: number;
  processed?: number;
  total?: number;
  rows?: number;
  elapsedSeconds?: number;
  etaSeconds?: number | null;
};

const BulkSearch = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resultsUrl, setResultsUrl] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<SearchStatus | null>(null);
  const searchIdRef = React.useRef<string | null>(null);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      selectedFiles: [],
    },
    validationSchema: Yup.object({
      selectedFiles: Yup.array()
        .min(1, "At least one file is required")
        .required("Required"),
    }),
    onSubmit: async (values: any) => {
      const searchId = newSearchId();
      searchIdRef.current = searchId;

      const formData = new FormData();

      values.selectedFiles.forEach((file: File) => {
        formData.append("files", file, file.name);
      });
      // The id travels with the upload so the server can publish progress
      // against it while this same request is still running.
      formData.append("data", JSON.stringify({ searchId }));

      try {
        setLoading(true);
        setSubmitted(true);
        setError("");
        setProgress(0);
        setStatus(null);

        const response: any = await bulkSearch(formData);
        setResultsUrl(response?.xlsx?.url || "");
        setProgress(100);
      } catch (bulkSearchError) {
        toast.error("An error occurred while processing the bulk search");
        setError("An error occurred while processing the bulk search");
      } finally {
        setLoading(false);
        searchIdRef.current = null;
      }
    },
  });

  const startNewSearch = useCallback(() => {
    setSubmitted(false);
    validation.resetForm();
    setProgress(0);
    setStatus(null);
    setResultsUrl("");
    setError("");
  }, [validation]);

  const downloadTemplate = useCallback(async () => {
    setDownloading(true);

    try {
      const response: any = await downloadBulkSearchTemplate();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Bulk_Search_Template.csv");
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      toast.error("An error occurred while downloading the bulk search template");
    } finally {
      setDownloading(false);
    }
  }, []);

  // Polls the running search for its real position. The upload request is
  // still open at this point; this is a separate, cheap request alongside it.
  useEffect(() => {
    if (!loading) return;

    let cancelled = false;

    const poll = async () => {
      const id = searchIdRef.current;
      if (!id) return;

      try {
        const response: any = await bulkSearchProgress(id, {});
        if (cancelled || !response?.found) return;

        setStatus(response);
        // Never let the bar go backwards. A poll can be overtaken by the next
        // one, and a reading that slipped would look like the run stalled.
        setProgress((current) =>
          Math.max(current, Math.min(99, Number(response.percent) || 0))
        );
      } catch (progressError) {
        // Progress is advisory. A failed poll leaves the last reading in place
        // rather than disturbing a search that is still running perfectly well.
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [loading]);

  // What the bar says beneath the percentage: the phase, and how far through it.
  const progressDetail = React.useMemo(() => {
    if (error) return "";
    if (!loading) return "";
    if (!status?.label) return "Preparing…";

    const parts = [status.label];
    if (status.phase === "searching" && status.total) {
      parts.push(
        `${(status.processed || 0).toLocaleString()} of ${status.total.toLocaleString()} names`
      );
    } else if (status.rows) {
      parts.push(`${status.rows.toLocaleString()} rows`);
    }
    return parts.join(" · ");
  }, [status, loading, error]);

  const timeRemaining = React.useMemo(() => {
    if (error) return "Failed";
    if (!loading) return "Processed";
    if (status?.etaSeconds == null) return "Estimating…";
    return `${formatDuration(status.etaSeconds)} remaining`;
  }, [status, loading, error]);

  return (
    <Card>
      <CardBody>
        {submitted ? (
          <>
            <Card className="bg-light overflow-hidden shadow-none">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <h6 className="mb-0">
                      <b
                        className={error ? "text-danger" : "text-secondary"}
                      >
                        {progress}%
                      </b>{" "}
                      {loading
                        ? "Searching..."
                        : error
                        ? "Failed"
                        : "Completed!"}
                    </h6>
                    {progressDetail && (
                      <small className="text-muted">{progressDetail}</small>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <h6 className="mb-0">
                      {error ? "Failed" : timeRemaining}
                    </h6>
                    {loading && !!status?.elapsedSeconds && (
                      <small className="text-muted d-block text-end">
                        {formatDuration(status.elapsedSeconds)} elapsed
                      </small>
                    )}
                  </div>
                </div>
              </CardBody>
              <Progress
                value={progress}
                color={error ? "danger" : "primary"}
                className="bg-info-subtle rounded-0"
              />
            </Card>

            {loading ? (
              <Skeleton
                height={200}
                baseColor="#F1F4F7"
                highlightColor="#E3E8ED"
              />
            ) : error ? (
              <Alert color="danger" className="material-shadow">
                <strong>
                  An error occurred while processing the bulk search.{" "}
                </strong>
                Please try again later or contact the Risk Management Unit if
                the issue persists.
              </Alert>
            ) : (
              <Alert color="primary" className="material-shadow">
                <strong>
                  Good news! Your bulk search has been successfully completed.
                </strong>{" "}
                You can now download the results in Excel format.
              </Alert>
            )}

            <div className="d-flex justify-content-between pt-3">
              <button
                type="button"
                className="btn btn-info"
                onClick={startNewSearch}
                disabled={loading}
              >
                <i className="ri-restart-line me-1"></i>
                New Bulk Search
              </button>

              {!loading && !error && (
                <Link
                  className="btn btn-primary"
                  to={resultsUrl}
                  target="_blank"
                >
                  <i className="ri-download-2-line me-1"></i>
                  Download Results
                </Link>
              )}
            </div>
          </>
        ) : (
          <Form
            className="tablelist-form"
            onSubmit={(event) => {
              event.preventDefault();
              validation.handleSubmit();
            }}
          >
            <SelectSearchFile validation={validation} />

            <div className="d-flex justify-content-between pt-4">
              <button
                type="button"
                className="btn btn-light"
                onClick={downloadTemplate}
                disabled={downloading}
              >
                {downloading && (
                  <Spinner color="light" size="sm" className="me-2" />
                )}
                <i className="ri-file-download-line me-1"></i>
                Download Template
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading || validation.values.selectedFiles.length === 0
                }
              >
                {loading && <Spinner color="light" size="sm" className="me-2" />}
                <i className="ri-search-line me-1"></i>
                Search
              </button>
            </div>
          </Form>
        )}
      </CardBody>
    </Card>
  );
};

export default BulkSearch;
